import { getMatchingName, getQueryFilter } from '@skytech/utils'
import minBy from 'lodash/minBy'

import {
  getIsInStock,
  getNumberOfProductsInStock,
  inventoryStringMatcher,
  parseInStock,
} from '@/modules/ecom-policy/helpers'
import type {
  CategoryData,
  ProductExtended,
  ProductGroupDataExtended,
} from '@/modules/ecom-policy/types'

const getMatchingNameDataList = ({
  stockFilter,
  searchQuery,
  product,
  matchingName,
  groupedAndFiltered,
  onlySelectedFilter,
  onlyMandatoryFilter,
  accessoriesByProductId,
  groupedAndFilteredGroupKeys,
  isInStock,
  isCategoryVisible,
  isSelected,
  isMandatory,
  isAccessoryOnlyMode,
  isOnlySelectedProductsAccessory,
}: {
  stockFilter: boolean
  product: ProductExtended
  matchingName: string
  groupedAndFiltered: Record<string, ProductGroupDataExtended>
  onlySelectedFilter: boolean
  onlyMandatoryFilter: boolean
  accessoriesByProductId: Record<number, number[]> | null
  groupedAndFilteredGroupKeys: string[]
  searchQuery?: string
  isInStock: boolean
  isCategoryVisible: boolean
  isSelected: boolean
  isMandatory: boolean
  isAccessoryOnlyMode: boolean
  isOnlySelectedProductsAccessory: boolean
}) => {
  const groupKey = matchingName + ' - ' + product.category
  const updatedGroupedProducts = [...(groupedAndFiltered[groupKey]?.groupedProducts || []), product]

  const isStockConditionMet = stockFilter ? isInStock : true
  const isQueryConditionMet = Boolean(
    updatedGroupedProducts.find(getQueryFilter(searchQuery, inventoryStringMatcher))
  )
  // All or only accessories
  const isProductTypeConditionMet = isAccessoryOnlyMode ? isOnlySelectedProductsAccessory : true
  const isOnlySelectedConditionMet = onlySelectedFilter ? isSelected : true
  const isOnlyMandatoryConditionMet = onlyMandatoryFilter ? isMandatory : true
  const isFiltersConditionMet =
    isQueryConditionMet && isStockConditionMet && isOnlySelectedConditionMet

  const selectedProducts = updatedGroupedProducts.filter(({ isSelected }) => isSelected)

  const isGroupSelected = selectedProducts.length > 0
  const hasAccessories = Boolean(
    accessoriesByProductId && updatedGroupedProducts.find(({ id }) => accessoriesByProductId[id])
  )
  const isProductVisible =
    isQueryConditionMet &&
    isStockConditionMet &&
    isCategoryVisible &&
    isOnlySelectedConditionMet &&
    isOnlyMandatoryConditionMet &&
    isProductTypeConditionMet
  const priceFrom =
    minBy(isGroupSelected ? selectedProducts : updatedGroupedProducts, product => product.price)
      ?.price || 0
  const hasCategoryBeenAddedToAGroup = groupedAndFilteredGroupKeys.includes(groupKey)
  const numberOfProductsInStock = getNumberOfProductsInStock(
    isGroupSelected ? selectedProducts : updatedGroupedProducts
  )

  return {
    numberOfProductsInStock,
    priceFrom,
    groupKey,
    updatedGroupedProducts,
    isGroupSelected,
    isFiltersConditionMet,
    isProductVisible,
    isProductTypeConditionMet,
    hasAccessories,
    hasCategoryBeenAddedToAGroup,
  }
}

const getNoMatchingNameDataList = ({
  stockFilter,
  product,
  searchQuery,
  onlySelectedFilter,
  onlyMandatoryFilter,
  productName,
  accessoriesByProductId,
  isInStock,
  isSelected,
  isAccessoryOnlyMode,
  isOnlySelectedProductsAccessory,
  isCategoryVisible,
  isMandatory,
}: {
  stockFilter: boolean
  onlySelectedFilter: boolean
  onlyMandatoryFilter: boolean
  product: ProductExtended
  productName: string
  searchQuery: string
  accessoriesByProductId: Record<number, number[]> | null
  isInStock: boolean
  isSelected: boolean
  isMandatory: boolean
  isAccessoryOnlyMode: boolean
  isOnlySelectedProductsAccessory: boolean
  isCategoryVisible: boolean
}) => {
  const isStockConditionMet = stockFilter ? isInStock : true
  const isQueryConditionMet = Boolean(
    [product].find(getQueryFilter(searchQuery, inventoryStringMatcher))
  )
  const isOnlySelectedConditionMet = onlySelectedFilter ? isSelected : true
  const isOnlyMandatoryConditionMet = onlyMandatoryFilter ? isMandatory : true
  // All or only accessories
  const isProductTypeConditionMet = isAccessoryOnlyMode ? isOnlySelectedProductsAccessory : true
  const isFiltersConditionMet =
    isQueryConditionMet && isStockConditionMet && isOnlySelectedConditionMet

  const isProductVisible =
    isQueryConditionMet &&
    isStockConditionMet &&
    isCategoryVisible &&
    isOnlySelectedConditionMet &&
    isOnlyMandatoryConditionMet &&
    isProductTypeConditionMet

  const hasAccessories = Boolean(accessoriesByProductId?.[product.id])
  const groupKey = productName + ' - ' + product.category

  return {
    groupKey,
    isProductVisible,
    isFiltersConditionMet,
    isProductTypeConditionMet,
    hasAccessories,
  }
}

export const getGroupedDataList = ({
  selectedCategoryIds,
  stockFilter,
  searchQuery,
  onlySelectedFilter,
  onlyMandatoryFilter,
  priceList,
  groupNames,
  everSelectedCategoryIds,
  productsWithVisibleAccessoriesIds,
  accessoriesByProductId,
}: {
  selectedCategoryIds: number[] | null
  stockFilter: boolean
  searchQuery: string
  onlySelectedFilter: boolean
  onlyMandatoryFilter: boolean
  priceList: ProductExtended[] | undefined
  groupNames?: string[]
  everSelectedCategoryIds: number[] | null
  productsWithVisibleAccessoriesIds: number[] | null
  accessoriesByProductId: Record<number, number[]> | null
}) => {
  const groupedAndFilteredGroupKeys: string[] = []
  if (!priceList || !groupNames) {
    return { groupedAndFiltered: {}, availableCategories: [] }
  }

  const expiredSelectedProductIds: Record<string, number[]> = {}
  const mandatorySelectedProductIds: Record<string, number[]> = {}

  const list = priceList.reduce(
    (acc, product) => {
      const {
        category,
        price,
        id: productId,
        name: productName,
        catId: categoryId,
        imgUrl: imageUrl,
        desc: description,
        currency,
        inStock,
        isSelected,
        vatPct,
        isExpired,
        isMandatory,
        alias: productAlias,
      } = product

      const isCategoryIncluded = everSelectedCategoryIds?.includes(categoryId)
      const isCategoryVisible = selectedCategoryIds?.length
        ? selectedCategoryIds?.includes(categoryId)
        : true
      const isInStock = Boolean(getIsInStock(product))
      const isAccessoryOnlyMode = Boolean(productsWithVisibleAccessoriesIds)
      const isOnlySelectedProductsAccessory = Boolean(
        accessoriesByProductId &&
          productsWithVisibleAccessoriesIds &&
          productsWithVisibleAccessoriesIds.find(id =>
            accessoriesByProductId[id]?.includes(productId)
          )
      )
      const isProductExpired = Boolean(isExpired)
      const isProductMandatory = Boolean(isMandatory)

      const matchingName = getMatchingName({
        groupNames,
        productName,
      })
      if (!matchingName) {
        const {
          groupKey,
          isProductVisible,
          isFiltersConditionMet,
          hasAccessories,
          isProductTypeConditionMet,
        } = getNoMatchingNameDataList({
          stockFilter,
          product,
          searchQuery,
          onlySelectedFilter,
          onlyMandatoryFilter,
          productName,
          accessoriesByProductId,
          isInStock,
          isSelected: Boolean(isSelected),
          isMandatory: Boolean(isMandatory),
          isAccessoryOnlyMode,
          isOnlySelectedProductsAccessory,
          isCategoryVisible,
        })

        if (isProductTypeConditionMet && (isFiltersConditionMet || isCategoryIncluded)) {
          const isCategoryEmpty = Boolean(isCategoryIncluded && !isFiltersConditionMet)

          groupedAndFilteredGroupKeys.push(groupKey)

          acc.availableCategories.push({ category, categoryId, isCategoryEmpty })
        }

        if (isProductVisible)
          acc.groupedAndFiltered[productId] = {
            description,
            category,
            currency,
            imageUrl,
            groupKey,
            inStock: parseInStock(inStock),
            vatPct,
            isSelected: Boolean(isSelected),
            groupedProducts: [product],
            groupName: productAlias || productName,
            priceFrom: price,
            hasAccessories,
            isExpired: isProductExpired && Boolean(isSelected),
            isMandatory: isProductMandatory && Boolean(isSelected),
          }

        return acc
      }

      const {
        numberOfProductsInStock,
        priceFrom,
        groupKey,
        updatedGroupedProducts,
        isGroupSelected,
        isFiltersConditionMet,
        isProductVisible,
        isProductTypeConditionMet,
        hasAccessories,
        hasCategoryBeenAddedToAGroup,
      } = getMatchingNameDataList({
        stockFilter,
        searchQuery,
        product,
        matchingName,
        groupedAndFiltered: acc.groupedAndFiltered,
        onlySelectedFilter,
        isMandatory: Boolean(isMandatory),
        onlyMandatoryFilter,
        accessoriesByProductId,
        groupedAndFilteredGroupKeys,
        isInStock,
        isCategoryVisible,
        isSelected: Boolean(isSelected),
        isAccessoryOnlyMode,
        isOnlySelectedProductsAccessory,
      })

      if (isExpired) {
        if (expiredSelectedProductIds[groupKey]) {
          const isProductIncluded = expiredSelectedProductIds[groupKey].find(id => id === productId)

          if (!isSelected && isProductIncluded) {
            expiredSelectedProductIds[groupKey] = expiredSelectedProductIds[groupKey].filter(
              id => id !== productId
            )
          }

          if (isSelected && !isProductIncluded) {
            expiredSelectedProductIds[groupKey] = [
              ...expiredSelectedProductIds[groupKey],
              productId,
            ]
          }
        } else if (isSelected) {
          expiredSelectedProductIds[groupKey] = [productId]
        }
      }

      if (isMandatory) {
        if (mandatorySelectedProductIds[groupKey]) {
          const isProductIncluded = mandatorySelectedProductIds[groupKey].find(
            id => id === productId
          )

          if (!isSelected && isProductIncluded) {
            mandatorySelectedProductIds[groupKey] = mandatorySelectedProductIds[groupKey].filter(
              id => id !== productId
            )
          }

          if (isSelected && !isProductIncluded) {
            mandatorySelectedProductIds[groupKey] = [
              ...mandatorySelectedProductIds[groupKey],
              productId,
            ]
          }
        } else if (isSelected) {
          mandatorySelectedProductIds[groupKey] = [productId]
        }
      }

      if (isProductTypeConditionMet && (isFiltersConditionMet || isCategoryIncluded)) {
        const isCategoryEmpty = Boolean(isCategoryIncluded && !isFiltersConditionMet)
        const hasEmptyCategory = acc.availableCategories.find(
          availableCategory =>
            availableCategory.categoryId === categoryId &&
            availableCategory.isCategoryEmpty === true
        )

        if (!hasCategoryBeenAddedToAGroup && !isCategoryEmpty) {
          acc.availableCategories.push({ category, categoryId, isCategoryEmpty })
          groupedAndFilteredGroupKeys.push(groupKey)
        }

        if (isCategoryEmpty && !hasEmptyCategory) {
          acc.availableCategories.push({ category, categoryId, isCategoryEmpty })
        }
      }

      if (isProductVisible) {
        acc.groupedAndFiltered[groupKey] = {
          groupKey,
          priceFrom,
          currency,
          category,
          vatPct,
          inStock: numberOfProductsInStock,
          isSelected: isGroupSelected,
          groupedProducts: updatedGroupedProducts,
          groupName:
            updatedGroupedProducts.length === 1 ? productAlias || productName : matchingName,
          description: description,
          imageUrl: imageUrl,
          hasAccessories,
          isExpired: expiredSelectedProductIds[groupKey]?.length > 0,
          isMandatory: mandatorySelectedProductIds[groupKey]?.length > 0,
        }
      }

      return acc
    },
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    { groupedAndFiltered: {}, availableCategories: [] } as {
      groupedAndFiltered: Record<string, ProductGroupDataExtended>
      availableCategories: CategoryData[]
    }
  )

  return list
}
